import {
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
	OnChanges,
	WritableSignal,
	signal,
	inject
} from "@angular/core";
import { Platform } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import * as _ from "lodash-es";
import { DateTime } from "luxon";
import { Subject, Subscription } from "rxjs";
import { take, takeUntil } from "rxjs/operators";
import { GetHeaderState, ResetHeaderState } from "src/app/shared/actions/utility.actions";
import { TypeHeader } from "src/app/shared/enums/type-header";
import {
	IEvent,
	IEventUser,
	IModule,
	ISurvey,
	ISurveyQuestion,
	ISurveyQuestionAnswer,
	ISurveyQuestionResult
} from "src/app/shared/interfaces";
import { getCurrentEventUser } from "src/app/shared/selectors/auth.selectors";
import { getCurrentEvent } from "src/app/shared/selectors/events.selectors";
import { getSpecificSurvey } from "src/app/shared/selectors/interactivity.selectors";
import { getSpecificModule } from "src/app/shared/selectors/modules.selectors";
import { selectRouteNestedParams, selectUrl } from "src/app/shared/selectors/router.selectors";
import {
	AnalyticsService,
	DocumentsService,
	FirestoreService,
	StorageService,
	SurveysService,
	UtilityService
} from "src/app/shared/services";
import { environment } from "src/environments/environment";
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from "@angular/material/core";
import { LuxonDateAdapter, MAT_LUXON_DATE_ADAPTER_OPTIONS } from "@angular/material-luxon-adapter";
import { DATE_PICKER_FORMAT } from "../../components/calendar/calendar.component";

@Component({
	selector: "app-survey",
	templateUrl: "./survey.component.html",
	styleUrls: ["./survey.component.scss"],
	standalone: false,
	providers: [
		{
			provide: DateAdapter,
			useClass: LuxonDateAdapter,
			deps: [MAT_DATE_LOCALE, MAT_LUXON_DATE_ADAPTER_OPTIONS]
		},
		{ provide: MAT_DATE_FORMATS, useValue: DATE_PICKER_FORMAT }
	]
})
export class SurveyComponent implements OnInit, OnDestroy, OnChanges {
	SUtility = inject(UtilityService);
	private readonly _adapter = inject<DateAdapter<unknown, unknown>>(DateAdapter);
	destroyComponent = new Subject();
	subscriptions: Subscription[] = [];

	loaderDoc: boolean = false;
	loadingSend: WritableSignal<boolean> = signal(false);

	@Input() componentMode: boolean = false;
	@Output() changeView: EventEmitter<{ segmentType: string; type: string; uid: string }> = new EventEmitter();

	@Input() eventId: string;
	event: IEvent;
	@Input() moduleId: string; // Survey module id
	module: IModule;
	@Input() surveyId: string;
	survey: ISurvey;
	@Input() sessionId: string;
	eventUser: IEventUser;
	@Input() showBackBtn: boolean;

	viewOnly: boolean = true;

	isMobile: boolean = false;

	questionsForm: WritableSignal<any> = signal({});

	currentLanguage: WritableSignal<string> = signal(environment.platform.defaultLanguage);
	extension: string;
	assetFileName: string;
	documentName: string;
	documentType: string;

	constructor(
		private SAnalytics: AnalyticsService,
		private platform: Platform,
		private store: Store,
		private SSurveys: SurveysService,
		private STranslate: TranslateService,
		private SStorage: StorageService,
		private SFirestore: FirestoreService,
		public SDocument: DocumentsService
	) {
		this.currentLanguage.set(this.STranslate.currentLang);
		this.setAdapterLocale();
		this.STranslate.onLangChange.pipe(takeUntil(this.destroyComponent)).subscribe((lang) => {
			this.currentLanguage.set(lang.lang);
			this.setAdapterLocale();
		});
	}

	ngOnInit() {
		this.isMobile =
			(this.platform.is("mobile") && window.innerWidth < 768) ||
			this.platform.is("mobileweb") ||
			window.innerWidth < 768
				? true
				: false;
		if (this.componentMode) {
			this.initDatas();
		}
	}

	ionViewWillEnter() {
		if (!this.componentMode) {
			this.store
				.select(selectUrl)
				.pipe(take(1))
				.subscribe(() => {
					this.store
						.select(selectRouteNestedParams)
						.pipe(take(1))
						.subscribe((params) => {
							this.eventId = params.eventId;
							this.moduleId = params.moduleId;
							this.surveyId = params.surveyId;
							this.subscriptions.forEach((sub) => sub.unsubscribe());

							// Analytics
							this.SAnalytics.surveyAccess(this.eventId, this.moduleId, this.surveyId);

							this.initDatas();
						});
				});
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes && changes.surveyId && !changes.surveyId.firstChange) {
			this.subscriptions.forEach((sub) => sub?.unsubscribe());
			this.event = null;
			this.module = null;
			this.survey = null;
			this.eventUser = null;
			this.initDatas();
		}
	}

	initDatas() {
		this.getEvent();
		this.getModule();
		this.getEventUser();
		this.getSurvey();
	}

	ionViewWillLeave() {
		if (!this.componentMode) {
			this.survey = null;
			this.store.dispatch(ResetHeaderState(null));
		}
		this.subscriptions.forEach((sub) => sub.unsubscribe());
	}

	/**
	 * Unsubscribe all subscriptions
	 */
	ngOnDestroy() {
		this.subscriptions.forEach((sub) => sub.unsubscribe());
		this.destroyComponent.complete();
	}

	setAdapterLocale() {
		this._adapter.setLocale(
			this.currentLanguage().slice(0, 2).toLowerCase() + "-" + this.currentLanguage().slice(2, 4).toUpperCase()
		);
	}

	/**
	 * Get event
	 */
	getEvent() {
		this.subscriptions.push(
			this.store.select(getCurrentEvent).subscribe((event) => {
				if (!_.isEqual(this.event, event)) {
					this.event = event;
				}
			})
		);
	}

	/**
	 * Getting module
	 */
	getModule() {
		this.subscriptions.push(
			this.store.select(getSpecificModule(this.moduleId)).subscribe((module) => {
				if (!_.isEqual(this.module, module)) {
					this.module = module;
				}
			})
		);
	}

	/**
	 * Get survey
	 */
	getSurvey() {
		this.subscriptions.push(
			this.store.select(getSpecificSurvey(this.surveyId)).subscribe((survey) => {
				if (!_.isEqual(this.survey, survey)) {
					this.survey = survey;
					if (this.survey) {
						this.survey.questions = this.survey.questions.filter((question) => question.visibility);
					}
					if (
						!this.survey ||
						(this.survey && !this.survey.visibility) ||
						(this.survey && this.survey.questions.length === 0)
					) {
						this.goBackToList();
						return;
					}
					if (this.survey) {
						this.patchResultsForm();
					}
				}

				if (this.survey && !this.componentMode) {
					this.store.dispatch(
						GetHeaderState({
							payload: {
								item: this.survey,
								title: this.survey.name,
								type: TypeHeader.SURVEY
							}
						})
					);
				}
			})
		);
	}

	/**
	 * Get event user
	 */
	getEventUser() {
		this.subscriptions.push(
			this.store.select(getCurrentEventUser).subscribe((eventUser) => {
				if (!_.isEqual(this.eventUser, eventUser)) {
					this.eventUser = eventUser;
				}
			})
		);
	}

	/**
	 * Patch form with all survey result
	 */
	patchResultsForm() {
		this.survey.questions.forEach((question) => {
			const resultOfQuestion = this.getResultOfQuestion(question);

			if (question.type === "oneSelect") {
				this.questionsForm.update((data) => {
					data[question.uid] = {
						value: resultOfQuestion ? resultOfQuestion.oneSelect.answerId : "",
						disabled: !this.checkCanChangeAnswer(question)
					};
					return { ...data };
				});
			} else if (question.type === "multipleSelect") {
				this.questionsForm.update((data) => {
					data[question.uid] = {
						value: resultOfQuestion ? resultOfQuestion.multipleSelect.answersIds : [],
						disabled: !this.checkCanChangeAnswer(question)
					};
					return { ...data };
				});
			} else if (question.type === "dissertative" || question.type === "plainText") {
				this.questionsForm.update((data) => {
					data[question.uid] = {
						value: resultOfQuestion ? resultOfQuestion.dissertative : "",
						disabled: !this.checkCanChangeAnswer(question)
					};
					return { ...data };
				});
			} else if (question.type === "date") {
				this.questionsForm.update((data) => {
					data[question.uid] = {
						value: resultOfQuestion ? resultOfQuestion.date : "",
						disabled: !this.checkCanChangeAnswer(question)
					};
					return { ...data };
				});
			} else if (question.type === "document") {
				this.assetFileName =
					resultOfQuestion && resultOfQuestion.document
						? this.SDocument.getFomatForSVG(resultOfQuestion.document.docType)
						: "generic-file_ico";
				this.questionsForm.update((data) => {
					data[question.uid] = {
						value: resultOfQuestion && resultOfQuestion.document ? resultOfQuestion.document.url : "",
						uid: resultOfQuestion && resultOfQuestion.document ? resultOfQuestion.document.uid : "",
						docType: resultOfQuestion && resultOfQuestion.document ? resultOfQuestion.document.docType : "",
						name: resultOfQuestion && resultOfQuestion.document ? resultOfQuestion.document.name : "",
						disabled: !this.checkCanChangeAnswer(question)
					};
					return { ...data };
				});
			} else if (question.type === "evaluation") {
				this.questionsForm.update((data) => {
					data[question.uid] = {
						value: resultOfQuestion ? resultOfQuestion.evaluation : 0,
						disabled: !this.checkCanChangeAnswer(question)
					};
					return { ...data };
				});
			}
		});
	}

	/**
	 * Chek if can change answer
	 */
	checkCanChangeAnswer(question: ISurveyQuestion) {
		const result = this.getResultOfQuestion(question);
		return result && !this.survey.changeAnswers ? false : true;
	}

	/**
	 * Check if can send survey
	 * @returns
	 */
	checkCanSendSurvey() {
		// let check = true;
		const noResult = this.survey.questions.some((question) => !this.getResultOfQuestion(question));
		// this.survey.questions.forEach((question) => {
		// 	const result = this.getResultOfQuestion(question);
		// 	if (!result) {
		// 		check = false;
		// 	}
		// });
		return noResult ? noResult : this.survey.changeAnswers;
		// return this.survey.changeAnswers || (!this.survey.changeAnswers && !check) ? true : false;
	}

	/**
	 * Check if document is an image
	 * @param question
	 * @returns
	 */
	checkImg(question: ISurveyQuestion) {
		const result = this.getResultOfQuestion(question);
		if (result && result.document && result.document.docType.includes("image")) {
			return true;
		} else {
			if (this.questionsForm()[question.uid].value.includes("image")) {
				return true;
			}
			return false;
		}
	}

	/**
	 * Get result of question
	 * @param question
	 * @returns
	 */
	getResultOfQuestion(question: ISurveyQuestion) {
		return this.survey.eventUserResults.find(
			(result) => result.questionId === question.uid && result.userId === this.eventUser.uid
		);
	}

	/**
	 * Getting img from document type
	 * @param question
	 * @returns
	 */
	getImg(question: ISurveyQuestion) {
		return this.questionsForm()[question.uid].value;
	}

	/**
	 * Get document
	 */
	async getDocument(evt: any, question: ISurveyQuestion) {
		this.loaderDoc = true;
		const item: File = evt.target.files[0];
		try {
			const documentBase64 = await this.SStorage.fromFileToBase64(item);
			// get type from base64
			this.documentType = item.type;
			this.documentName = item.name;
			// const type = (documentBase64 as string).split(";base64,")[0].split("data:")[1];

			// get extension from type
			this.extension = this.SDocument.checkType(this.documentType.split("/")[1]);
			this.assetFileName = this.SDocument.getFomatForSVG(this.documentType.split("/")[1]);

			if (this.documentType.includes("image")) {
				this.questionsForm.update((data) => {
					data[question.uid].value = documentBase64;
					data[question.uid].docType = this.documentType;
					data[question.uid].name = this.documentName;
					return { ...data };
				});
			} else {
				this.questionsForm.update((data) => {
					data[question.uid].value = `${this.documentType},${documentBase64}`;
					data[question.uid].docType = this.documentType;
					data[question.uid].name = this.documentName;
					return { ...data };
				});
				// this.questionsForm.get(question.uid).patchValue(documentBase64);
			}
			this.loaderDoc = false;
		} catch (error) {
			this.loaderDoc = false;
		}
	}

	/**
	 * Send survey
	 */
	async sendSurvey() {
		try {
			if (this.loadingSend()) return;
			this.loadingSend.set(true);
			// Check force complete
			if (this.survey.forceComplete) {
				let check = true;
				const results = this.questionsForm();

				for (const key of Object.keys(results)) {
					const result = results[key].value;
					if (!result) {
						check = false;
					}
				}
				if (!check) {
					const alert = await this.SUtility.presentAlert(
						this.STranslate.instant("surveys.force_complete_title_alert"),
						this.STranslate.instant("surveys.force_complete_message_alert"),
						[
							{
								text: this.STranslate.instant("buttons.ok")
							}
						],
						[]
					);
					alert.present();
					return;
				}
			}

			const results: { result: ISurveyQuestionResult; new: boolean }[] = [];

			const datas = this.questionsForm();

			let points = 0;
			for (const key of Object.keys(datas)) {
				const formResult = datas[key];
				const result = _.cloneDeep(this.survey.eventUserResults.find((res) => res.questionId === key));
				const question = this.survey.questions.find((question) => question.uid === key);

				// Save image if document type
				const imageId: string = this.SFirestore.createId(
					`events/${this.eventId}/modules/${this.moduleId}/surveys/${this.survey.uid}/users-results`
				);
				let imageUrl: string = "";
				if (question.type === "document" && formResult.value && formResult.value !== "") {
					if (formResult.value.includes("firebasestorage.googleapis.com")) {
						imageUrl = formResult.value;
					} else {
						imageUrl = await this.SStorage.uploadBase64(
							formResult.value.split(";base64,")[1],
							this.documentType,
							// eslint-disable-next-line max-len
							`events/${this.eventId}/modules/${this.moduleId}/surveys/${this.survey.uid}/users-results/${this.eventUser.uid}/${this.eventUser.uid}.jpg`
						);
					}
				}
				if (result) {
					if (question.type === "oneSelect") {
						result.oneSelect = {
							answerId: formResult.value
						};
					} else if (question.type === "multipleSelect") {
						result.multipleSelect = {
							answersIds: formResult.value
						};
					} else if (question.type === "dissertative" || question.type === "plainText") {
						result.dissertative = formResult.value;
					} else if (question.type === "date") {
						result.date =
							typeof formResult.value === "string" ? formResult.value : formResult.value.toISO();
					} else if (question.type === "document") {
						result.document = {
							uid: imageId ? imageId : "",
							docType: formResult.docType,
							name: formResult.name,
							url: imageUrl ? imageUrl : ""
						};
					} else if (question.type === "evaluation") {
						result.evaluation = formResult.value;
					}

					points +=
						question.type === "oneSelect"
							? question.answers.find((answer) => answer.uid === result.oneSelect.answerId).weight
							: question.type === "multipleSelect"
								? question.answers
										.filter((answer) => result.multipleSelect.answersIds.includes(answer.uid))
										.reduce((sum, answer) => sum + answer.weight, 0)
								: question.weight;

					results.push({ result: result, new: false });
				} else if (
					(question.type === "multipleSelect" && formResult.value.length > 0) ||
					(question.type !== "multipleSelect" && formResult.value)
				) {
					const newResult: ISurveyQuestionResult = {
						uid: this.SFirestore.createId(
							`events/${this.eventId}/modules/${this.moduleId}/surveys/${this.survey.uid}/users-results`
						),
						creationDate: DateTime.local().toISO(),
						date:
							question.type === "date"
								? typeof formResult.value === "string"
									? formResult.value
									: formResult.value.toISO()
								: null,
						dissertative:
							question.type === "dissertative" || question.type === "plainText" ? formResult.value : null,
						document:
							question.type === "document"
								? {
										uid: imageId ? imageId : "",
										docType: formResult.docType,
										name: formResult.name,
										url: imageUrl ? imageUrl : ""
									}
								: null,
						evaluation: question.type === "evaluation" ? formResult.value : null,
						eventId: this.eventId,
						moduleId: this.moduleId,
						multipleSelect:
							question.type === "multipleSelect"
								? {
										answersIds: formResult.value
									}
								: null,
						oneSelect:
							question.type === "oneSelect"
								? {
										answerId: formResult.value
									}
								: null,
						questionId: question.uid,
						surveyId: this.survey.uid,
						type: question.type,
						userId: this.eventUser.uid
					};

					points +=
						question.type === "oneSelect"
							? question.answers.find((answer) => answer.uid === newResult.oneSelect.answerId).weight
							: question.type === "multipleSelect"
								? question.answers
										.filter((answer) => newResult.multipleSelect.answersIds.includes(answer.uid))
										.reduce((sum, answer) => sum + answer.weight, 0)
								: question.weight;

					results.push({ result: newResult, new: true });
				}
			}
			await this.SSurveys.createSurveyResults(
				this.eventId,
				this.moduleId,
				this.surveyId,
				results,
				this.eventUser,
				this.survey.weight,
				points
			);

			// if (results.length === this.survey.questions.length) {
			const alert = await this.SUtility.presentAlert(
				this.STranslate.instant("alerts.thankyou_answer"),
				this.STranslate.instant("alerts.answer_saved_successfully"),
				[
					{
						text: "Ok",
						handler: () => {
							this.goBackToList();
						}
					}
				],
				[],
				false
			);
			alert.present();
			// }
			this.loadingSend.set(false);

			// Analytics
			this.SAnalytics.sendSurvey(
				this.eventId,
				this.moduleId,
				this.eventUser,
				this.surveyId,
				results.map((result) => result.result)
			);

			// this.SUtility.presentToast(this.STranslate.instant("surveys.survey_send"), 3000, "bottom", "success");
			this.goBackToList();
		} catch (error) {
			this.SUtility.presentToast(this.STranslate.instant("surveys.cannot_send_survey"), 3000, "bottom", "danger");
			this.loadingSend.set(false);
		}
	}

	/**
	 * Go back to surveys list
	 */
	goBackToList() {
		if (this.componentMode) {
			this.changeView.emit({ segmentType: "surveys", type: "surveys", uid: "" });
		} else {
			this.SUtility.callGoBackOnHeader();
		}
	}

	selectOption(question: ISurveyQuestion, option: ISurveyQuestionAnswer) {
		if (question.type === "oneSelect") {
			this.questionsForm.update((data) => {
				data[question.uid].value = option.uid;
				return { ...data };
			});
		} else {
			this.questionsForm.update((data) => {
				data[question.uid].value = this.questionsForm()[question.uid].value.includes(option.uid)
					? this.questionsForm()[question.uid].value.filter((uid) => uid !== option.uid)
					: [...this.questionsForm()[question.uid].value, option.uid];
				return { ...data };
			});
		}
	}

	isChecked(question: ISurveyQuestion, answer: ISurveyQuestionAnswer) {
		if (question.type === "oneSelect") {
			return this.questionsForm()[question.uid].value === answer.uid;
		} else {
			return this.questionsForm()[question.uid].value.includes(answer.uid);
		}
	}

	updateEvaluation(question: ISurveyQuestion, value: number) {
		this.questionsForm.update((data) => {
			data[question.uid].value = value;
			return { ...data };
		});
	}
}
